/**
 * 分拣机 openapi 接口的 HMAC-SHA256 签名（前端复刻 apifox 预处理脚本）。
 * 用浏览器内置 Web Crypto（crypto.subtle），不引第三方加密库。
 *
 * stringToSign = [appKey, timestamp, nonce, contentSha256, METHOD, path, queryString].join("\n")
 * 头：X-App-Key / X-Timestamp / X-Nonce / X-Content-SHA256 / X-Signature
 */

const encoder = new TextEncoder();

function toHex(buffer: ArrayBuffer): string {
    return Array.from(new Uint8Array(buffer))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}

/** SHA-256(text) 小写十六进制 */
export async function sha256Hex(text: string): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', encoder.encode(text));
    return toHex(digest);
}

/** HMAC-SHA256(message, secret) 小写十六进制 */
export async function hmacSha256Hex(message: string, secret: string): Promise<string> {
    const key = await crypto.subtle.importKey(
        'raw',
        encoder.encode(secret),
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign'],
    );
    const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(message));
    return toHex(signature);
}

/** 32 位小写十六进制随机 nonce（与脚本口径一致） */
export function randomNonceHex32(): string {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    return Array.from(bytes).map((b) => (b % 16).toString(16)).join('');
}

export interface SignInput {
    appKey: string;
    appSecret: string;
    method: string;
    /** URL path，如 /api/v4/openapi/getChuteConfig */
    path: string;
    /** query string，格式 a=1&b=2（无则空串） */
    queryString: string;
    /** 请求体原文（GET 无体传空串） */
    rawBody: string;
}

export interface SignResult {
    headers: Record<string, string>;
    /** 便于页面「请求预览」展示中间值 */
    timestamp: string;
    nonce: string;
    contentSha256: string;
    signature: string;
    stringToSign: string;
}

/** 计算签名并返回需附加的 5 个请求头 */
export async function buildSignatureHeaders(input: SignInput): Promise<SignResult> {
    const timestamp = Math.floor(Date.now() / 1000).toString();
    const nonce = randomNonceHex32();
    const contentSha256 = await sha256Hex(input.rawBody || '');
    const stringToSign = [
        input.appKey,
        timestamp,
        nonce,
        contentSha256,
        input.method.toUpperCase(),
        input.path,
        input.queryString,
    ].join('\n');
    const signature = await hmacSha256Hex(stringToSign, input.appSecret);
    return {
        headers: {
            'X-App-Key': input.appKey,
            'X-Timestamp': timestamp,
            'X-Nonce': nonce,
            'X-Content-SHA256': contentSha256,
            'X-Signature': signature,
        },
        timestamp,
        nonce,
        contentSha256,
        signature,
        stringToSign,
    };
}

/** 当前秒级时间戳（requestTimestamp 字段默认值用） */
export function currentUnixSeconds(): number {
    return Math.floor(Date.now() / 1000);
}
